import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Search,
  X,
  Send,
  ArrowLeft,
  MessageCircle,
  Check,
  CheckCheck,
  Users,
  Plus,
  UserPlus,
  Bookmark,
  Clock,
  Settings,
  Timer
} from "lucide-react";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import UserBubble from "./UserBubble";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Helper function to get glow class based on engagement status
const getEngagementGlowClass = (entity) => {
  if (entity?.engagement_status === 'chatting' || entity?.is_chatting) {
    return 'chatting-glow';
  } else if (entity?.engagement_status === 'online' || entity?.is_online) {
    return 'online-glow';
  }
  return '';
};

// Chat/Messenger Page Component with Group Chat Support
export const ChatPage = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Group chat creation state
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupSearchQuery, setGroupSearchQuery] = useState('');
  const [groupSearchResults, setGroupSearchResults] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [creatingGroup, setCreatingGroup] = useState(false);
  
  // RonRo AI assistant state
  const [ronroUserId, setRonroUserId] = useState(null);
  const [awaitingRonroReply, setAwaitingRonroReply] = useState(false);
  
  // Fetch RonRo's user ID on mount
  useEffect(() => {
    const fetchRonroId = async () => {
      try {
        const response = await axios.get(`${API}/ronro/user-id`);
        if (response.data.exists) {
          setRonroUserId(response.data.user_id);
        }
      } catch (error) {
        console.log('RonRo not found');
      }
    };
    fetchRonroId();
  }, []);

  const fetchConversations = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await axios.get(`${API}/conversations`);
      const convos = response.data || [];
      setConversations(convos);
      return convos;
    } catch (error) {
      console.error("Error fetching conversations:", error);
      return [];
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // Handle URL conversation parameter - open specific conversation
  useEffect(() => {
    const conversationIdFromUrl = searchParams.get('conversation');
    const userIdFromUrl = searchParams.get('user');
    
    // Handle direct user message from Discover Golfers
    if (userIdFromUrl) {
      const startChatWithUser = async () => {
        try {
          // Fetch user info to start a conversation
          const response = await axios.get(`${API}/users/${userIdFromUrl}/public-profile`);
          const targetUser = response.data;
          
          if (targetUser) {
            // Check if conversation already exists
            const convos = await fetchConversations(true);
            const existingConvo = convos.find(c => 
              !c.is_group && c.other_user?.user_id === userIdFromUrl
            );
            
            if (existingConvo) {
              // Open existing conversation
              setSelectedConversation(existingConvo);
              const msgResponse = await axios.get(`${API}/conversations/${existingConvo.conversation_id}/messages`);
              setMessages(msgResponse.data || []);
              setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
            } else {
              // Start new conversation (temp)
              const tempConv = {
                conversation_id: null,
                is_group: false,
                other_user: {
                  user_id: userIdFromUrl,
                  name: targetUser.name,
                  username: targetUser.username,
                  picture: targetUser.picture
                },
                participant_ids: [user?.user_id, userIdFromUrl]
              };
              setSelectedConversation(tempConv);
              setMessages([]);
              setTimeout(() => inputRef.current?.focus(), 300);
            }
          }
        } catch (error) {
          console.log('Failed to start chat with user:', error);
          toast.error('Could not start chat with this user');
        }
        setSearchParams({}, { replace: true });
      };
      startChatWithUser();
      return;
    }
    
    if (!conversationIdFromUrl) return;
    
    const openConversation = async () => {
      // Fetch the specific conversation directly
      try {
        const response = await axios.get(`${API}/conversations/${conversationIdFromUrl}`);
        if (response.data) {
          setSelectedConversation(response.data);
          // Fetch messages for this conversation
          const msgResponse = await axios.get(`${API}/conversations/${conversationIdFromUrl}/messages`);
          setMessages(msgResponse.data || []);
          setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
          // Add to conversations list if not there
          setConversations(prev => {
            if (!prev.find(c => c.conversation_id === conversationIdFromUrl)) {
              return [response.data, ...prev];
            }
            return prev;
          });
        }
      } catch (error) {
        console.log('Conversation not found:', error);
        // Refresh conversations and try again
        const freshConvos = await fetchConversations(true);
        const targetConvo = freshConvos.find(c => c.conversation_id === conversationIdFromUrl);
        if (targetConvo) {
          setSelectedConversation(targetConvo);
          // Fetch messages
          try {
            const msgResponse = await axios.get(`${API}/conversations/${conversationIdFromUrl}/messages`);
            setMessages(msgResponse.data || []);
            setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
          } catch (e) {
            console.log('Failed to fetch messages:', e);
          }
        }
      }
      setSearchParams({}, { replace: true });
    };
    
    openConversation();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Initial fetch and polling for conversations
  useEffect(() => {
    fetchConversations();
    
    // Poll for new conversations every 10 seconds
    const interval = setInterval(() => {
      fetchConversations(true); // Silent refresh
    }, 10000);
    
    return () => clearInterval(interval);
  }, [fetchConversations]);

  // Search for users to message
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }
    
    try {
      setIsSearching(true);
      const response = await axios.get(`${API}/members`, {
        params: { search: searchQuery }
      });
      setSearchResults(response.data.members?.filter(m => m.user_id !== user?.user_id) || []);
    } catch (error) {
      console.error("Error searching:", error);
    }
  }, [searchQuery, user?.user_id]);

  useEffect(() => {
    const debounceTimer = setTimeout(handleSearch, 300);
    return () => clearTimeout(debounceTimer);
  }, [handleSearch]);

  // Search for users to add to group
  const handleGroupSearch = useCallback(async () => {
    if (!groupSearchQuery.trim()) {
      setGroupSearchResults([]);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/members`, {
        params: { search: groupSearchQuery }
      });
      // Filter out current user and already selected members
      const selectedIds = selectedMembers.map(m => m.user_id);
      setGroupSearchResults(
        response.data.members?.filter(m => 
          m.user_id !== user?.user_id && !selectedIds.includes(m.user_id)
        ) || []
      );
    } catch (error) {
      console.error("Error searching:", error);
    }
  }, [groupSearchQuery, user?.user_id, selectedMembers]);

  useEffect(() => {
    const debounceTimer = setTimeout(handleGroupSearch, 300);
    return () => clearTimeout(debounceTimer);
  }, [handleGroupSearch]);

  const fetchMessages = useCallback(async (conversationId, silent = false) => {
    try {
      const response = await axios.get(`${API}/conversations/${conversationId}/messages`);
      const newMessages = response.data || [];
      
      // Only scroll if there are new messages
      setMessages(prev => {
        const hasNewMessages = newMessages.length !== prev.length;
        if (hasNewMessages && !silent) {
          setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
        }
        return newMessages;
      });
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  }, []);

  // Poll for new messages when a conversation is selected
  useEffect(() => {
    if (!selectedConversation?.conversation_id) return;
    
    // Poll for new messages every 5 seconds
    const interval = setInterval(() => {
      fetchMessages(selectedConversation.conversation_id, true);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [selectedConversation?.conversation_id, fetchMessages]);

  const handleSelectConversation = async (conv) => {
    setSelectedConversation(conv);
    setSearchQuery('');
    setSearchResults([]);
    setIsSearching(false);
    await fetchMessages(conv.conversation_id);
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      inputRef.current?.focus();
    }, 300);
  };

  const handleStartConversation = async (member) => {
    const tempConv = {
      conversation_id: null,
      is_group: false,
      other_user: {
        user_id: member.user_id,
        name: member.name,
        username: member.username,
        picture: member.picture
      },
      participant_ids: [user.user_id, member.user_id]
    };
    setSelectedConversation(tempConv);
    setMessages([]);
    setSearchQuery('');
    setSearchResults([]);
    setIsSearching(false);
    setTimeout(() => inputRef.current?.focus(), 300);
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim()) {
      toast.error("Please enter a group name");
      return;
    }
    if (selectedMembers.length < 1) {
      toast.error("Please add at least one member");
      return;
    }

    setCreatingGroup(true);
    try {
      const response = await axios.post(`${API}/conversations`, {
        participant_ids: selectedMembers.map(m => m.user_id),
        is_group: true,
        name: groupName.trim()
      });

      toast.success("Group created!");
      setShowCreateGroup(false);
      setGroupName('');
      setSelectedMembers([]);
      setGroupSearchQuery('');
      setGroupSearchResults([]);
      
      // Open the new group conversation
      fetchConversations();
      setSelectedConversation({
        ...response.data,
        is_group: true
      });
      setMessages([]);
    } catch (error) {
      console.error("Error creating group:", error);
      toast.error("Failed to create group");
    } finally {
      setCreatingGroup(false);
    }
  };

  const addMemberToGroup = (member) => {
    setSelectedMembers(prev => [...prev, member]);
    setGroupSearchQuery('');
    setGroupSearchResults([]);
  };

  const removeMemberFromGroup = (memberId) => {
    setSelectedMembers(prev => prev.filter(m => m.user_id !== memberId));
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    const messageText = newMessage.trim();
    setNewMessage('');
    setSendingMessage(true);
    
    const optimisticMsg = {
      message_id: `temp_${Date.now()}`,
      sender_id: user?.user_id,
      sender_name: user?.name,
      content: messageText,
      created_at: new Date().toISOString(),
      status: 'sending'
    };
    setMessages(prev => [...prev, optimisticMsg]);
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    
    try {
      let conversationId = selectedConversation.conversation_id;
      
      if (!conversationId) {
        const otherUserId = selectedConversation.other_user?.user_id;
        const convResponse = await axios.post(`${API}/conversations`, {
          participant_ids: [otherUserId],
          is_group: false
        });
        conversationId = convResponse.data.conversation_id;
        
        setSelectedConversation(prev => ({
          ...prev,
          conversation_id: conversationId
        }));
      }
      
      const response = await axios.post(`${API}/conversations/${conversationId}/messages`, {
        content: messageText
      });
      
      const realMsg = response.data;
      setMessages(prev => prev.map(m => 
        m.message_id === optimisticMsg.message_id ? { ...realMsg, status: 'sent' } : m
      ));
      
      fetchConversations();
      
      // Check if this is a message to RonRo - trigger AI response
      const isMessageToRonro = selectedConversation?.other_user?.user_id === ronroUserId ||
                               selectedConversation?.participant_ids?.includes(ronroUserId);
      
      if (isMessageToRonro && ronroUserId && !selectedConversation?.is_group) {
        setAwaitingRonroReply(true);
        try {
          const ronroResponse = await axios.post(`${API}/ronro/chat`, {
            conversation_id: conversationId,
            message: messageText
          });
          
          if (ronroResponse.data.success) {
            // Add RonRo's response to messages
            const ronroMsg = {
              message_id: ronroResponse.data.message_id,
              sender_id: ronroUserId,
              sender_name: 'RonRo',
              content: ronroResponse.data.response,
              created_at: new Date().toISOString(),
              status: 'sent',
              is_ronro_response: true
            };
            setMessages(prev => [...prev, ronroMsg]);
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }
        } catch (error) {
          console.error('RonRo response error:', error);
          // Don't show error to user - RonRo just won't respond
        } finally {
          setAwaitingRonroReply(false);
        }
      }
    } catch (error) {
      console.error("Failed to send message:", error);
      setMessages(prev => prev.map(m => 
        m.message_id === optimisticMsg.message_id ? { ...m, status: 'failed' } : m
      ));
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  // Save/unsave a message to prevent auto-deletion
  const handleSaveMessage = async (messageId, isSaved) => {
    try {
      if (isSaved) {
        await axios.delete(`${API}/messages/${messageId}/save`);
        toast.success("Message will now auto-delete");
      } else {
        await axios.post(`${API}/messages/${messageId}/save`);
        toast.success("Message saved");
      }
      // Update the message in state
      setMessages(prev => prev.map(m => 
        m.message_id === messageId ? { ...m, is_saved: !isSaved } : m
      ));
    } catch (error) {
      console.error("Failed to save/unsave message:", error);
      toast.error("Failed to update message");
    }
  };

  // Toggle disappearing messages for this conversation
  const handleToggleDisappearing = async () => {
    if (!selectedConversation?.conversation_id) return;
    
    try {
      const response = await axios.post(
        `${API}/conversations/${selectedConversation.conversation_id}/toggle-disappearing`
      );
      
      // Update the conversation in state
      setSelectedConversation(prev => ({
        ...prev,
        disappearing_enabled: response.data.disappearing_enabled
      }));
      
      // Also update in conversations list
      setConversations(prev => prev.map(c => 
        c.conversation_id === selectedConversation.conversation_id
          ? { ...c, disappearing_enabled: response.data.disappearing_enabled }
          : c
      ));
      
      toast.success(response.data.message);
    } catch (error) {
      console.error("Failed to toggle disappearing messages:", error);
      toast.error(error.response?.data?.detail || "Failed to update setting");
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    if (diffHours < 48) return 'Yesterday';
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const groupMessagesByDate = (msgs) => {
    const groups = [];
    let currentDate = null;
    
    msgs.forEach((msg) => {
      const msgDate = new Date(msg.created_at).toDateString();
      if (msgDate !== currentDate) {
        currentDate = msgDate;
        groups.push({ type: 'date', date: msg.created_at });
      }
      groups.push({ type: 'message', ...msg });
    });
    
    return groups;
  };

  const formatDateHeader = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / 86400000);
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'long' });
    return date.toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' });
  };

  // Get display info for a conversation
  const getConversationDisplay = (conv) => {
    if (conv.is_group) {
      return {
        name: conv.name || 'Group Chat',
        picture: null,
        isGroup: true,
        memberCount: conv.participant_ids?.length || 0,
        userId: null
      };
    }
    return {
      name: conv.other_user?.name || 'Unknown',
      picture: conv.other_user?.picture,
      isGroup: false,
      userId: conv.other_user?.user_id
    };
  };

  // Get sender name for group messages
  const getSenderName = (msg, conv) => {
    if (!conv.is_group) return null;
    if (msg.sender_id === user?.user_id) return null;
    
    // Try to find sender in participants
    const sender = conv.participants?.find(p => p.user_id === msg.sender_id);
    return sender?.name?.split(' ')[0] || msg.sender_name?.split(' ')[0] || 'Unknown';
  };

  // Chat View
  if (selectedConversation) {
    const groupedMessages = groupMessagesByDate(messages);
    const isGroup = selectedConversation.is_group;
    const displayInfo = getConversationDisplay(selectedConversation);
    
    return (
      <div className="flex flex-col bg-[var(--ps-bg-base)] h-full" data-testid="chat-view">
        {/* Chat Header - Fixed at top */}
        <div className="flex items-center gap-2 px-3 py-2 bg-[var(--ps-bg-surface)] border-b border-[var(--ps-border)] flex-shrink-0">
          <button 
            onClick={() => setSelectedConversation(null)}
            className="p-1 rounded-full hover:bg-[var(--ps-bg-hover)] text-[var(--ps-text-secondary)] transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          
          {isGroup ? (
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[var(--ps-primary)] to-[var(--ps-gold)] flex items-center justify-center">
              <Users className="w-4 h-4 text-white" />
            </div>
          ) : (
            <Avatar className="w-9 h-9 ring-2 ring-[var(--ps-primary)]/20">
              <AvatarImage src={displayInfo.picture} />
              <AvatarFallback className="bg-[var(--ps-primary)] text-white font-bold text-sm">
                {displayInfo.name?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
          )}
          
          <div className="flex-1 min-w-0">
            <h3 
              className={`font-bold text-base text-[var(--ps-text-primary)] truncate ${!isGroup && displayInfo.userId ? 'cursor-pointer hover:underline' : ''}`}
              onClick={() => !isGroup && displayInfo.userId && navigate(`/profile/${displayInfo.userId}`)}
              data-testid="chat-header-name"
            >
              {displayInfo.name}
            </h3>
            {isGroup ? (
              <p className="text-xs text-[var(--ps-text-muted)]">
                {displayInfo.memberCount} members
              </p>
            ) : selectedConversation?.disappearing_enabled && (
              <p className="text-xs text-[var(--ps-text-muted)] flex items-center gap-1">
                <Timer className="w-3 h-3" />
                Messages delete after 24h
              </p>
            )}
          </div>
          
          {/* Disappearing messages toggle - only for 1-on-1 chats */}
          {!isGroup && (
            <button
              onClick={handleToggleDisappearing}
              className={`p-1.5 rounded-full transition-colors ${
                selectedConversation?.disappearing_enabled
                  ? 'text-[var(--ps-primary)] bg-[var(--ps-primary)]/10'
                  : 'text-[var(--ps-text-muted)] hover:bg-[var(--ps-bg-hover)]'
              }`}
              title={selectedConversation?.disappearing_enabled ? "Disappearing messages ON" : "Disappearing messages OFF"}
              data-testid="toggle-disappearing-btn"
            >
              <Timer className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Profile Card - Only for 1-on-1 chats */}
        {!isGroup && displayInfo.userId && (
          <div 
            className="flex-shrink-0 mx-3 mt-2 p-3 bg-gradient-to-r from-pink-500/10 to-purple-500/10 border border-pink-500/20 rounded-xl cursor-pointer hover:border-pink-500/40 transition-colors"
            onClick={() => navigate(`/profile/${displayInfo.userId}`)}
          >
            <div className="flex items-center gap-3">
              <Avatar className="w-12 h-12 ring-2 ring-pink-500/30">
                <AvatarImage src={displayInfo.picture} />
                <AvatarFallback className="bg-pink-500/20 text-pink-400 font-bold">
                  {displayInfo.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <p className="text-white font-semibold">{displayInfo.name}</p>
                <p className="text-gray-400 text-sm">Tap to view profile</p>
              </div>
            </div>
          </div>
        )}

        {/* Scrollable area for messages only */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-3 py-2" style={{ minHeight: 0 }}>
            {/* Messages */}
            {messages.length === 0 ? (
              <div className="flex flex-col items-center py-4 text-center">
              {isGroup ? (
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[var(--ps-primary)] to-[var(--ps-gold)] flex items-center justify-center mb-2">
                  <Users className="w-7 h-7 text-white" />
                </div>
              ) : (
                <Avatar className="w-14 h-14 mb-2 ring-3 ring-[var(--ps-primary)]/20">
                  <AvatarImage src={displayInfo.picture} />
                  <AvatarFallback className="bg-[var(--ps-primary)] text-white text-lg font-bold">
                    {displayInfo.name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
              )}
              <h3 className="text-sm font-bold text-[var(--ps-text-primary)]">
                {displayInfo.name}
              </h3>
              <p className="text-xs text-[var(--ps-text-muted)] mt-0.5">
                {isGroup ? 'Start the group conversation' : 'Start chatting'}
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {groupedMessages.map((item, index) => {
                if (item.type === 'date') {
                  return (
                    <div key={`date-${index}`} className="flex justify-center my-2">
                      <span className="text-[10px] text-[var(--ps-text-muted)] bg-[var(--ps-bg-elevated)] px-2 py-0.5 rounded-full">
                        {formatDateHeader(item.date)}
                      </span>
                    </div>
                  );
                }
                
                const msg = item;
                const isOwn = msg.sender_id === user?.user_id;
                const senderName = getSenderName(msg, selectedConversation);
                const showAvatar = !isOwn && (
                  index === groupedMessages.length - 1 || 
                  groupedMessages[index + 1]?.type === 'date' ||
                  groupedMessages[index + 1]?.sender_id !== msg.sender_id
                );
                
                // Check if we should show sender name (first message from this sender in a row)
                const prevItem = groupedMessages[index - 1];
                const showSenderName = isGroup && !isOwn && (
                  !prevItem || 
                  prevItem.type === 'date' || 
                  prevItem.sender_id !== msg.sender_id
                );
                
                return (
                  <div 
                    key={msg.message_id || index}
                    className={`flex items-end gap-1 ${isOwn ? 'justify-end' : 'justify-start'} group`}
                  >
                    {!isOwn && (
                      <div className="w-5 h-5 flex-shrink-0">
                        {showAvatar && (
                          <Avatar className="w-5 h-5">
                            <AvatarFallback className="bg-[var(--ps-primary)] text-white text-[8px]">
                              {senderName?.charAt(0) || displayInfo.name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                        )}
                      </div>
                    )}
                    <div className={`max-w-[80%] relative`}>
                      {showSenderName && senderName && (
                        <p 
                          className="text-[10px] text-[var(--ps-primary)] ml-1 mb-0.5 font-medium cursor-pointer hover:underline"
                          onClick={() => msg.sender_id && navigate(`/profile/${msg.sender_id}`)}
                          data-testid={`msg-sender-${msg.message_id}`}
                        >
                          {senderName}
                        </p>
                      )}
                      <div className={`rounded-2xl px-3 py-1.5 ${
                        isOwn 
                          ? 'bg-[var(--ps-primary)] text-white rounded-br-sm' 
                          : 'bg-[var(--ps-bg-surface)] text-[var(--ps-text-primary)] border border-[var(--ps-border)] rounded-bl-sm'
                      } ${msg.status === 'failed' ? 'opacity-60' : ''}`}>
                        <p className="text-[13px] leading-snug whitespace-pre-wrap break-words">{msg.content}</p>
                        {/* Saved indicator */}
                        {msg.is_saved && (
                          <Bookmark className="w-3 h-3 absolute -top-1 -right-1 text-[var(--ps-gold)] fill-[var(--ps-gold)]" />
                        )}
                      </div>
                      {/* Save button - appears on hover/tap */}
                      {selectedConversation?.disappearing_enabled && (
                        <button
                          onClick={() => handleSaveMessage(msg.message_id, msg.is_saved)}
                          className={`absolute ${isOwn ? '-left-6' : '-right-6'} top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-full hover:bg-[var(--ps-bg-hover)]`}
                          title={msg.is_saved ? "Unsave message" : "Save message"}
                          data-testid={`save-msg-${msg.message_id}`}
                        >
                          <Bookmark className={`w-4 h-4 ${msg.is_saved ? 'text-[var(--ps-gold)] fill-[var(--ps-gold)]' : 'text-[var(--ps-text-muted)]'}`} />
                        </button>
                      )}
                      {msg.status === 'failed' && (
                        <p className="text-[9px] text-[var(--ps-error)] mt-0.5 text-right">Tap to retry</p>
                      )}
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
          
          {/* RonRo Typing Indicator */}
          {awaitingRonroReply && (
            <div className="flex items-center gap-2 px-3 py-2">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white text-xs font-bold">
                R
              </div>
              <div className="bg-[var(--ps-bg-elevated)] rounded-xl px-3 py-2">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-[var(--ps-text-muted)] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-[var(--ps-text-muted)] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-[var(--ps-text-muted)] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
              <span className="text-xs text-[var(--ps-text-muted)]">RonRo is typing...</span>
            </div>
          )}
        </div>

        {/* Message Input - fixed at bottom */}
        <div className="flex-shrink-0 px-3 py-2 border-t border-[var(--ps-border)] bg-[var(--ps-bg-base)]" style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 8px)' }}>
          <div className="flex items-center gap-1.5 bg-[var(--ps-bg-surface)] rounded-full px-2 py-1 border border-[var(--ps-border)]">
            <Input
              ref={inputRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Message..."
              className="flex-1 bg-transparent border-0 text-[var(--ps-text-primary)] placeholder:text-[var(--ps-text-muted)] px-2 py-1 text-sm h-8 focus:ring-0 focus:outline-none"
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              data-testid="message-input"
            />
            <Button 
              onClick={handleSendMessage}
              disabled={!newMessage.trim() || sendingMessage}
              size="sm"
              className={`rounded-full w-8 h-8 p-0 flex items-center justify-center flex-shrink-0 ${
                newMessage.trim() 
                  ? 'bg-[var(--ps-primary)] hover:bg-[var(--ps-primary-dark)] text-white' 
                  : 'bg-[var(--ps-bg-elevated)] text-[var(--ps-text-muted)]'
              }`}
              data-testid="send-message-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Conversations List
  return (
    <div className="bg-[var(--ps-bg-base)]" style={{ minHeight: 'calc(100dvh - 120px)' }} data-testid="chat-page">
      {/* Header with New Group Button */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <h1 className="text-2xl font-black text-[var(--ps-text-primary)]">Chats</h1>
        <Button
          onClick={() => setShowCreateGroup(true)}
          size="sm"
          className="bg-[var(--ps-primary)] hover:bg-[var(--ps-primary-dark)] text-white rounded-full h-8 px-3 text-xs"
          data-testid="new-group-btn"
        >
          <Users className="w-3.5 h-3.5 mr-1" />
          New Group
        </Button>
      </div>

      {/* Search Bar */}
      <div className="px-4 pb-3">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[var(--ps-text-muted)]" />
          <Input
            type="text"
            placeholder="Search golfers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 pr-10 w-full bg-[var(--ps-bg-elevated)] border-[var(--ps-border)] text-[var(--ps-text-primary)] placeholder:text-[var(--ps-text-muted)] rounded-full"
            data-testid="chat-search-input"
          />
          {searchQuery && (
            <button
              onClick={() => { setSearchQuery(''); setSearchResults([]); setIsSearching(false); }}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[var(--ps-text-muted)] hover:text-[var(--ps-text-primary)]"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Search Results */}
      {isSearching && searchQuery && (
        <div className="px-4 pb-4">
          <p className="text-xs font-medium text-[var(--ps-text-muted)] uppercase tracking-wider mb-2 px-2">
            Search Results
          </p>
          {searchResults.length === 0 ? (
            <p className="text-center py-4 text-[var(--ps-text-muted)]">
              No golfers found
            </p>
          ) : (
            <div className="space-y-1">
              {searchResults.map((member) => (
                <button
                  key={member.user_id}
                  onClick={() => handleStartConversation(member)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-[var(--ps-bg-hover)] transition-colors text-left"
                  data-testid={`search-result-${member.user_id}`}
                >
                  <UserBubble
                    userId={member.user_id}
                    picture={member.picture}
                    name={member.name}
                    bubbleColor={member.bubble_color || 'green'}
                    isOnline={member.is_online}
                    size="lg"
                    clickable={false}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[var(--ps-text-primary)] truncate">{member.name}</p>
                    {member.username && (
                      <p className="text-sm text-[var(--ps-text-muted)]">@{member.username}</p>
                    )}
                  </div>
                  <MessageCircle className="w-5 h-5 text-[var(--ps-primary)]" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Conversations List */}
      {!isSearching && (
        <div className="px-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="dark-spinner" />
            </div>
          ) : conversations.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 rounded-full bg-[var(--ps-primary)]/10 flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-10 h-10 text-[var(--ps-primary)]" />
              </div>
              <h3 className="text-lg font-semibold text-[var(--ps-text-primary)] mb-2">No messages yet</h3>
              <p className="text-sm text-[var(--ps-text-secondary)] mb-4">
                Search for golfers or create a group
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {conversations.map((conv) => {
                const display = getConversationDisplay(conv);
                
                return (
                  <button
                    key={conv.conversation_id}
                    onClick={() => handleSelectConversation(conv)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-[var(--ps-bg-hover)] transition-colors text-left"
                    data-testid={`conversation-${conv.conversation_id}`}
                  >
                    <div className="relative">
                      {display.isGroup ? (
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[var(--ps-primary)] to-[var(--ps-gold)] flex items-center justify-center">
                          <Users className="w-6 h-6 text-white" />
                        </div>
                      ) : (
                        <UserBubble
                          userId={display.user_id}
                          picture={display.picture}
                          name={display.name}
                          bubbleColor={display.bubble_color || 'green'}
                          isOnline={display.is_online}
                          size="xl"
                          clickable={false}
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className={`font-semibold truncate text-[var(--ps-text-primary)] ${getEngagementGlowClass(display)}`}>
                            {display.name}
                          </span>
                          {/* Disappearing messages indicator */}
                          {conv.disappearing_enabled && !conv.is_group && (
                            <Timer className="w-3 h-3 text-[var(--ps-primary)] flex-shrink-0" />
                          )}
                        </div>
                        <span className="text-xs text-[var(--ps-text-muted)] flex-shrink-0 ml-2">
                          {formatTime(conv.last_message_at)}
                        </span>
                      </div>
                      <p className={`text-sm truncate ${conv.unread_count > 0 ? 'text-[var(--ps-text-primary)] font-medium' : 'text-[var(--ps-text-secondary)]'}`}>
                        {conv.last_message || 'Start a conversation'}
                      </p>
                    </div>
                    {conv.unread_count > 0 && (
                      <span className="bg-[var(--ps-primary)] text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0">
                        {conv.unread_count > 9 ? '9+' : conv.unread_count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Create Group Modal */}
      <Dialog open={showCreateGroup} onOpenChange={setShowCreateGroup}>
        <DialogContent className="bg-[var(--ps-bg-surface)] border-[var(--ps-border)] text-[var(--ps-text-primary)] max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[var(--ps-text-primary)] flex items-center gap-2">
              <Users className="w-5 h-5 text-[var(--ps-primary)]" />
              New Group Chat
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            {/* Group Name */}
            <div>
              <label className="text-sm font-medium text-[var(--ps-text-secondary)] mb-1 block">
                Group Name
              </label>
              <Input
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="e.g., Weekend Golfers"
                className="bg-[var(--ps-bg-elevated)] border-[var(--ps-border)] text-[var(--ps-text-primary)]"
                data-testid="group-name-input"
              />
            </div>

            {/* Selected Members */}
            {selectedMembers.length > 0 && (
              <div>
                <label className="text-sm font-medium text-[var(--ps-text-secondary)] mb-1 block">
                  Members ({selectedMembers.length})
                </label>
                <div className="flex flex-wrap gap-2">
                  {selectedMembers.map((member) => (
                    <div 
                      key={member.user_id}
                      className="flex items-center gap-1.5 bg-[var(--ps-primary)]/10 text-[var(--ps-primary)] px-2 py-1 rounded-full text-sm"
                    >
                      <span>{member.name?.split(' ')[0]}</span>
                      <button
                        onClick={() => removeMemberFromGroup(member.user_id)}
                        className="hover:bg-[var(--ps-primary)]/20 rounded-full p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add Members Search */}
            <div>
              <label className="text-sm font-medium text-[var(--ps-text-secondary)] mb-1 block">
                Add Members
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[var(--ps-text-muted)]" />
                <Input
                  value={groupSearchQuery}
                  onChange={(e) => setGroupSearchQuery(e.target.value)}
                  placeholder="Search golfers..."
                  className="pl-9 bg-[var(--ps-bg-elevated)] border-[var(--ps-border)] text-[var(--ps-text-primary)]"
                  data-testid="group-member-search"
                />
              </div>
              
              {/* Search Results */}
              {groupSearchResults.length > 0 && (
                <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
                  {groupSearchResults.map((member) => (
                    <button
                      key={member.user_id}
                      onClick={() => addMemberToGroup(member)}
                      className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-[var(--ps-bg-hover)] transition-colors text-left"
                    >
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={member.picture} />
                        <AvatarFallback className="bg-[var(--ps-primary)] text-white text-xs">
                          {member.name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm text-[var(--ps-text-primary)] flex-1 truncate">
                        {member.name}
                      </span>
                      <UserPlus className="w-4 h-4 text-[var(--ps-primary)]" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowCreateGroup(false);
                setGroupName('');
                setSelectedMembers([]);
                setGroupSearchQuery('');
              }}
              className="border-[var(--ps-border)] text-[var(--ps-text-secondary)]"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateGroup}
              disabled={!groupName.trim() || selectedMembers.length < 1 || creatingGroup}
              className="bg-[var(--ps-primary)] hover:bg-[var(--ps-primary-dark)] text-white"
              data-testid="create-group-btn"
            >
              {creatingGroup ? 'Creating...' : 'Create Group'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChatPage;
